import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function BranchesPage() {
  const { hasPerm } = useAuth();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "", code: "", address: "", venue_capacity: 200,
    tax_rate: 0.12, service_charge_rate: 0.10,
  });

  const load = () => api.get("/branches").then(({data}) => { setBranches(data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/branches", { ...form, venue_capacity: parseInt(form.venue_capacity), tax_rate: parseFloat(form.tax_rate), service_charge_rate: parseFloat(form.service_charge_rate) });
      toast.success("Branch created");
      setCreating(false);
      setForm({ name: "", code: "", address: "", venue_capacity: 200, tax_rate: 0.12, service_charge_rate: 0.10 });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="branches-page">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="tiny-label text-violet-400">Multi-branch</div>
          <h1 className="mt-1 text-4xl font-extrabold tracking-tight">Branches</h1>
        </div>
        {hasPerm("branch.manage") && (
          <button onClick={() => setCreating(true)} data-testid="new-branch-btn" className="h-11 px-4 rounded-lg bg-violet-600 hover:bg-violet-500 text-white font-semibold shadow-[0_0_20px_rgba(139,92,246,0.35)]">+ New branch</button>
        )}
      </div>

      {loading && <div className="text-zinc-500">Loading…</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {branches.map((b) => (
          <div key={b.id} className="panel-elevated p-6 hover:border-violet-500/40 transition-colors" data-testid={`branch-card-${b.code}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="tiny-label">{b.code}</div>
                <h3 className="mt-1 text-xl font-bold">{b.name}</h3>
              </div>
              <span className={`tiny-label ${b.active ? "text-emerald-400" : "text-zinc-500"}`}>{b.active ? "ACTIVE" : "INACTIVE"}</span>
            </div>
            <p className="mt-2 text-sm text-zinc-400 line-clamp-2">{b.address}</p>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div><div className="tiny-label">Hours</div><div className="text-zinc-200 mt-0.5">{b.operating_hours}</div></div>
              <div><div className="tiny-label">Capacity</div><div className="text-zinc-200 mt-0.5">{b.venue_capacity}</div></div>
              <div><div className="tiny-label">Tax</div><div className="text-zinc-200 mt-0.5">{Math.round((b.tax_rate||0)*100)}%</div></div>
              <div><div className="tiny-label">Service charge</div><div className="text-zinc-200 mt-0.5">{Math.round((b.service_charge_rate||0)*100)}%</div></div>
            </div>
          </div>
        ))}
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="new-branch-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => setCreating(false)}/>
          <form onSubmit={submit} className="relative w-full max-w-md bg-zinc-950 border-l border-white/10 h-full p-6 overflow-y-auto shadow-2xl">
            <div className="tiny-label text-violet-400">Branches</div>
            <h2 className="text-2xl font-bold">New branch</h2>
            <F label="Name"><input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} required className="i" data-testid="new-branch-name"/></F>
            <F label="Code"><input value={form.code} onChange={(e)=>setForm({...form, code: e.target.value})} required className="i" data-testid="new-branch-code" placeholder="e.g. OW-ORT"/></F>
            <F label="Address"><textarea value={form.address} onChange={(e)=>setForm({...form, address: e.target.value})} rows={2} className="i"/></F>
            <div className="grid grid-cols-3 gap-3">
              <F label="Capacity"><input type="number" value={form.venue_capacity} onChange={(e)=>setForm({...form, venue_capacity: e.target.value})} className="i"/></F>
              <F label="Tax %"><input type="number" step="0.01" value={form.tax_rate} onChange={(e)=>setForm({...form, tax_rate: e.target.value})} className="i"/></F>
              <F label="SC %"><input type="number" step="0.01" value={form.service_charge_rate} onChange={(e)=>setForm({...form, service_charge_rate: e.target.value})} className="i"/></F>
            </div>
            <div className="mt-6 flex gap-2">
              <button type="button" onClick={()=>setCreating(false)} className="h-11 px-4 rounded-lg bg-zinc-900 border border-white/10 text-zinc-300 flex-1">Cancel</button>
              <button type="submit" data-testid="new-branch-submit" className="h-11 px-4 rounded-lg bg-violet-600 hover:bg-violet-500 text-white font-bold flex-1">Create</button>
            </div>
            <style>{`.i { margin-top: 6px; height: 44px; width: 100%; border-radius: 8px; background: rgb(9 9 11); border: 1px solid rgba(255,255,255,0.10); padding: 0 14px; color: #fff; }`}</style>
          </form>
        </div>
      )}
    </div>
  );
}

function F({ label, children }) { return <label className="block mt-4"><div className="tiny-label">{label}</div>{children}</label>; }
